import React, { useMemo, useState } from 'react';
import DashboardBarChart from './charts/DashboardBarChart';
import {
    ALL_EMAIL_REMINDER_CATEGORIES,
    EMAIL_REMINDER_GROUPS,
    EMAIL_STATS_WINDOWS,
    emailStatKey,
} from '../dashboardStatsConfig';
import { parseStatValue } from '../dashboardStatUtils';
import { VIBRANT_PALETTE } from '../dashboardChartColors';

const SENT_COLOR = '#4154f1';
const OPENED_COLOR = '#198754';
const ALL_REMINDER_VALUE = 'ALL';
const GROUP_VALUE_PREFIX = 'GROUP:';

const GROUP_OPTION_LABELS = {
    patient: 'Patient Emails',
    system: 'System Emails',
};

const EMAIL_SCOPE_OPTIONS = [
    { value: ALL_REMINDER_VALUE, label: 'All', categories: ALL_EMAIL_REMINDER_CATEGORIES },
    ...EMAIL_REMINDER_GROUPS.map((group) => ({
        value: `${GROUP_VALUE_PREFIX}${group.id}`,
        label: GROUP_OPTION_LABELS[group.id] || group.label,
        categories: group.categories,
    })),
];

function sumCategoriesMetric(getStat, categories, metric, days) {
    return categories.reduce(
        (total, category) => total + parseStatValue(getStat(emailStatKey(category.keyPrefix, metric, days))),
        0
    );
}

function buildCategoryChartSeries(getStat, categories, days) {
    return categories
        .map((category, index) => {
            const reminderSent = parseStatValue(getStat(emailStatKey(category.keyPrefix, 'sent', days)));
            const reminderOpened = parseStatValue(getStat(emailStatKey(category.keyPrefix, 'opened', days)));
            const reminderRate =
                reminderSent > 0 ? Math.round((reminderOpened * 100) / reminderSent) : 0;

            return {
                key: category.keyPrefix,
                label: category.label,
                color: VIBRANT_PALETTE[index % VIBRANT_PALETTE.length],
                value: reminderSent,
                opened: reminderOpened,
                tooltipExtra: `Opened: ${reminderOpened.toLocaleString()} · Open rate: ${reminderRate}%`,
            };
        })
        .filter((item) => item.value > 0 || item.opened > 0)
        .sort((a, b) => b.value - a.value);
}

function DashboardEmailStats({ getStat, isLoading, loadError, lastUpdated }) {
    const [days, setDays] = useState(30);
    const [selectedReminder, setSelectedReminder] = useState(ALL_REMINDER_VALUE);

    const selectedScope = useMemo(
        () => EMAIL_SCOPE_OPTIONS.find((option) => option.value === selectedReminder) || null,
        [selectedReminder]
    );

    const isGroupedView = Boolean(selectedScope);
    const selectedCategory = useMemo(() => {
        if (isGroupedView) {
            return null;
        }

        return (
            ALL_EMAIL_REMINDER_CATEGORIES.find((category) => category.keyPrefix === selectedReminder) ||
            ALL_EMAIL_REMINDER_CATEGORIES[0]
        );
    }, [isGroupedView, selectedReminder]);

    const activeCategories = selectedScope?.categories || [];
    const sent = isGroupedView
        ? sumCategoriesMetric(getStat, activeCategories, 'sent', days)
        : parseStatValue(getStat(emailStatKey(selectedCategory.keyPrefix, 'sent', days)));
    const opened = isGroupedView
        ? sumCategoriesMetric(getStat, activeCategories, 'opened', days)
        : parseStatValue(getStat(emailStatKey(selectedCategory.keyPrefix, 'opened', days)));
    const openRate = sent > 0 ? Math.round((opened * 100) / sent) : 0;
    const hasData = sent > 0 || opened > 0;

    const chartSeries = useMemo(() => {
        if (isGroupedView) {
            return buildCategoryChartSeries(getStat, activeCategories, days);
        }

        return [
            { key: 'sent', label: 'Sent', color: SENT_COLOR, value: sent },
            { key: 'opened', label: 'Opened', color: OPENED_COLOR, value: opened },
        ];
    }, [activeCategories, days, getStat, isGroupedView, opened, sent]);

    const scopeLabel = selectedScope?.label || selectedCategory?.label || 'emails';
    const emptyLabel = isGroupedView
        ? selectedReminder === ALL_REMINDER_VALUE
            ? 'emails'
            : scopeLabel.toLowerCase()
        : selectedCategory.label.toLowerCase();

    const subtitle = lastUpdated
        ? isGroupedView
            ? selectedReminder === ALL_REMINDER_VALUE
                ? `Sent by reminder · Updated ${lastUpdated}`
                : `Sent by reminder in ${scopeLabel} · Updated ${lastUpdated}`
            : `Sent vs opened for ${selectedCategory.label} · Updated ${lastUpdated}`
        : 'Select a reminder to view sent vs opened';

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--green">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-envelope-paper" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">Email Stats</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">{subtitle}</p>
                        </div>
                    </div>
                    <div className="dashboard-email-stats__controls">
                        <label className="dashboard-email-stats__select-wrap">
                            <span className="dashboard-email-stats__select-label">Reminder</span>
                            <select
                                className="form-select form-select-sm dashboard-email-stats__select"
                                value={selectedReminder}
                                onChange={(event) => setSelectedReminder(event.target.value)}
                                aria-label="Email reminder type"
                            >
                                {EMAIL_SCOPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                                {EMAIL_REMINDER_GROUPS.map((group) => (
                                    <optgroup key={group.id} label={group.label}>
                                        {group.categories.map((category) => (
                                            <option key={category.keyPrefix} value={category.keyPrefix}>
                                                {category.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </label>
                        <div className="dashboard-email-stats__toggle" role="group" aria-label="Email stats period">
                            {EMAIL_STATS_WINDOWS.map((window) => (
                                <button
                                    key={window.days}
                                    type="button"
                                    className={`dashboard-email-stats__toggle-btn${days === window.days ? ' is-active' : ''}`}
                                    onClick={() => setDays(window.days)}
                                >
                                    {window.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="dashboard-stat-tile dashboard-stat-tile--skeleton" style={{ minHeight: 280 }} />
                ) : (
                    <>
                        <div className="dashboard-email-stats__summary">
                            <div className="dashboard-email-stats__kpi">
                                <span className="dashboard-email-stats__kpi-label">Sent</span>
                                <strong className="dashboard-email-stats__kpi-value">{sent.toLocaleString()}</strong>
                            </div>
                            <div className="dashboard-email-stats__kpi">
                                <span className="dashboard-email-stats__kpi-label">Opened</span>
                                <strong className="dashboard-email-stats__kpi-value">{opened.toLocaleString()}</strong>
                            </div>
                            <div className="dashboard-email-stats__kpi">
                                <span className="dashboard-email-stats__kpi-label">Open rate</span>
                                <strong className="dashboard-email-stats__kpi-value">{openRate}%</strong>
                            </div>
                        </div>

                        {!hasData ? (
                            <div className="dashboard-empty-state">
                                <i className="bi bi-bar-chart me-2" />
                                No {emptyLabel} in the last {days} days
                            </div>
                        ) : (
                            <div className="dashboard-email-stats__chart">
                                <DashboardBarChart
                                    series={chartSeries}
                                    solidColors
                                    showLegend
                                    horizontal={isGroupedView}
                                    seriesName={isGroupedView ? 'Sent' : 'Count'}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}

export default DashboardEmailStats;
